import { FormProvider, useForm } from 'react-hook-form'
import React, { useCallback, useContext, useState } from 'react'
import { StandardRegisterProps } from '.'
import { useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import { MergeCartsById, MergeCartsByIdMutation, MergeCartsByIdMutationVariables, RegisterConsumer, RegisterConsumerMutation, RegisterConsumerMutationVariables, RegisterCustomerStatus } from 'src/graphql/generated/graphql-types'
import { State } from 'src/components/registerBusinessLegacy/standardRegister'
import { validationSchemaAccount, validationSchemaEmail } from 'src/components/registerBusinessLegacy/standardRegister/validation'
import Error from 'src/components/registerBusinessLegacy/standardRegister/Error'
import cookieHandler from '@dustin-web/auth-js/dist/handlers/cookieHandler'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import CustomRedirect from 'src/components/customRedirect'
import AppContext from 'src/appContext'
import { accountCreated } from 'src/util/googleAnalyticsWrapperV2'
import Spinner from '@dustin-web/microsite-components/src/spinner'

const useYupValidationResolver = (state: State) =>
  useCallback(
    async (data: StandardRegisterProps) => {
      try {
        const schema = schemas[State[state]]

        const values = await schema.validate(data, {
          abortEarly: false,
        })

        return {
          values,
          errors: {},
        }
      } catch (errors: any) {
        return {
          values: {},
          errors: errors.inner.reduce(
            (allErrors: any, currentError: any) => ({
              ...allErrors,
              [currentError.path]: {
                type: currentError.type ?? 'validation',
                message: currentError.message,
              },
            }),
            {}
          ),
        }
      }
    },
    [state]
  )

const schemas: any = {
  email: validationSchemaEmail,
  account: validationSchemaAccount
}

type FormProps = {
  defaultValues: StandardRegisterProps
  state: State
  returnUrl: string | null
  onStateChange: (state: State) => void
  children: React.ReactNode
}

const Form: React.FC<FormProps> = ({ defaultValues, state, returnUrl, onStateChange, children }) => {
  const { sourcingCompany } = useContext(AppContext)
  const [status, setStatus] = useState<RegisterCustomerStatus | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const cookies = cookieHandler()
  const { setCurrentContact } = useAuth()
  const [redirectLink, setRedirectLink] = useState<string | null>(null)

  const methods = useForm<StandardRegisterProps>({
    resolver: useYupValidationResolver(state),
    defaultValues,
  })

  const [mergeCartsById] = useGraphqlMutation<MergeCartsByIdMutation, MergeCartsByIdMutationVariables>(
    MergeCartsById
  )

  const [mutation] = useGraphqlMutation<
    RegisterConsumerMutation,
    RegisterConsumerMutationVariables
  >(RegisterConsumer)

  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  const onSubmit = async ({ acceptAccountTerms, email, firstName, lastName, offersByEmail, password, preRegistrationToken }: StandardRegisterProps) => {
    if (state === State.email) {
      onStateChange(State.account)
    }
    else {
      setLoading(true)

      const result: RegisterConsumerMutation = await mutation({
        variables: {
          accountTermsAccepted: acceptAccountTerms,
          firstName,
          lastName,
          offersByEmail,
          email: email == '' ? defaultValues.email : email,
          password,
          preRegistrationToken
        }
      })

      if (
        result.registerConsumer?.status === RegisterCustomerStatus.Success
        && result?.registerConsumer?.contactId
      ) {
        const { cartId, isAuthenticated } = defaultValues

        accountCreated(sourcingCompany)

        if (isAuthenticated) {
          try {
            if (!!cartId) {
              await mergeCartsById({
                variables: {
                  sourceCartId: cartId,
                  targetContactId: result.registerConsumer.contactId,
                },
              })
            }
          } catch (error) {
            console.error('Failed to merge cart', {
              error,
              contactId: result.registerConsumer.contactId,
            })
          }

          await setCurrentContact(result.registerConsumer.contactId)
          setRedirectLink(returnUrl ?? '/')
        }
        else {
          cookies.setContactId(result.registerConsumer.contactId)
          onStateChange(State.confirmation)
        }
      }

      setStatus(result.registerConsumer?.status ?? null)
      setLoading(false)
    }
  }

  if (redirectLink) {
    return <CustomRedirect url={redirectLink} />
  }

  return (
    <FormProvider {...methods}>
      {
        loading &&
        <Spinner dustinSpinner />
      }
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        onKeyDown={checkKeyDown}>{children}</form>

      <Error email={methods.getValues("email")} returnUrl={returnUrl} status={status} />
    </FormProvider>
  )
}

export default Form
